import React, { useState, useEffect } from "react";
import {
  Modal,
  Card,
  DatePicker,
  Spin,
  message,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Grid,
} from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarElement,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DailyPayInfo {
  date: string;
  total_orders: number;
  paid_orders: number;
  refund_orders: number;
  revenue: number;
  refund_amount: number;
  net_revenue: number;
  postchat_revenue: number;
  summary_revenue: number;
  alipay_revenue: number;
  code_revenue: number;
}

interface DailyPayInfoResponse {
  date_range: string;
  days_count: number;
  summary: {
    total_revenue: number;
    total_refund: number;
    net_revenue: number;
    total_orders: number;
    total_paid_orders: number;
    total_refund_orders: number;
  };
  daily_data: DailyPayInfo[];
}

interface RevenueChartProps {
  visible: boolean;
  onCancel: () => void;
  defaultDateRange?: [dayjs.Dayjs, dayjs.Dayjs];
}

const timeRangePresets = [
  {
    label: "最近7天",
    value: [dayjs().subtract(6, "day"), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs],
  },
  {
    label: "最近15天",
    value: [dayjs().subtract(14, "day"), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs],
  },
  {
    label: "最近30天",
    value: [dayjs().subtract(29, "day"), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs],
  },
  {
    label: "最近60天",
    value: [dayjs().subtract(59, "day"), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs],
  },
  {
    label: "最近90天",
    value: [dayjs().subtract(89, "day"), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs],
  },
];

const RevenueChart: React.FC<RevenueChartProps> = ({
  visible,
  onCancel,
  defaultDateRange,
}) => {
  const { API_BASE_URL, token } = useGlobal();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DailyPayInfoResponse | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(
    defaultDateRange || [dayjs().subtract(29, "day"), dayjs()]
  );
  const [chartType, setChartType] = useState<"line" | "bar">("line");
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // 获取营收数据
  const fetchRevenueData = React.useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");

      const response = await request.get(
        `${API_BASE_URL}/api/v3/admin/orderList/payInfo/daily`,
        {
          params: {
            start_time: startDate,
            end_time: endDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        setData(response.data.data);
      } else {
        message.error(response.data.msg || "获取营收数据失败");
      }
    } catch (error) {
      console.error("获取营收数据失败:", error);
      message.error("获取营收数据失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, dateRange]);

  const formatDateForDisplay = (date: string) => {
    return dayjs(date).format(isMobile ? "MM/DD" : "MM-DD");
  };

  // 获取图表数据和配置
  const getChartConfig = () => {
    if (!data?.daily_data) return null;

    const labels = data.daily_data.map((item) =>
      formatDateForDisplay(item.date)
    );

    const datasets = [
      {
        label: "总营收",
        data: data.daily_data.map((item) => (item.revenue / 100).toFixed(2)),
        borderColor: "#1677ff",
        backgroundColor:
          chartType === "bar"
            ? "rgba(22, 119, 255, 0.6)"
            : "rgba(22, 119, 255, 0.1)",
        tension: 0.4,
        fill: chartType === "line",
        pointRadius: isMobile ? 3 : 4,
        pointHoverRadius: isMobile ? 4 : 6,
      },
      {
        label: "净营收",
        data: data.daily_data.map((item) =>
          (item.net_revenue / 100).toFixed(2)
        ),
        borderColor: "#52c41a",
        backgroundColor:
          chartType === "bar"
            ? "rgba(82, 196, 26, 0.6)"
            : "rgba(82, 196, 26, 0.1)",
        tension: 0.4,
        fill: chartType === "line",
        pointRadius: isMobile ? 3 : 4,
        pointHoverRadius: isMobile ? 4 : 6,
      },
      {
        label: "时长计费 营收",
        data: data.daily_data.map((item) =>
          (item.postchat_revenue / 100).toFixed(2)
        ),
        borderColor: "#fa8c16",
        backgroundColor:
          chartType === "bar"
            ? "rgba(250, 140, 22, 0.6)"
            : "rgba(250, 140, 22, 0.1)",
        tension: 0.4,
        fill: false,
        pointRadius: isMobile ? 3 : 4,
        pointHoverRadius: isMobile ? 4 : 6,
      },
      {
        label: "token计费 营收",
        data: data.daily_data.map((item) =>
          (item.summary_revenue / 100).toFixed(2)
        ),
        borderColor: "#722ed1",
        backgroundColor:
          chartType === "bar"
            ? "rgba(114, 46, 209, 0.6)"
            : "rgba(114, 46, 209, 0.1)",
        tension: 0.4,
        fill: false,
        pointRadius: isMobile ? 3 : 4,
        pointHoverRadius: isMobile ? 4 : 6,
      },
    ];

    const chartData = {
      labels,
      datasets,
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top" as const,
          labels: {
            usePointStyle: true,
            padding: isMobile ? 15 : 20,
            font: {
              size: isMobile ? 12 : 14,
            },
          },
        },
        tooltip: {
          mode: "index" as const,
          intersect: false,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleColor: "#fff",
          bodyColor: "#fff",
          borderColor: "#ddd",
          borderWidth: 1,
          callbacks: {
            label: function (context: any) {
              return `${context.dataset.label}: ¥${context.parsed.y}`;
            },
          },
        },
      },
      interaction: {
        mode: "nearest" as const,
        axis: "x" as const,
        intersect: false,
      },
      scales: {
        x: {
          display: true,
          title: {
            display: !isMobile,
            text: "时间",
            color: "#666",
          },
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            maxTicksLimit: isMobile ? 6 : 10,
            color: "#666",
            font: {
              size: isMobile ? 10 : 12,
            },
          },
        },
        y: {
          display: true,
          title: {
            display: !isMobile,
            text: "金额（元）",
            color: "#666",
          },
          beginAtZero: true,
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            color: "#666",
            font: {
              size: isMobile ? 10 : 12,
            },
            callback: function (value: any) {
              return `¥${value}`;
            },
          },
        },
      },
      elements: {
        point: {
          hoverBackgroundColor: "#fff",
          hoverBorderWidth: 2,
        },
      },
      animation: {
        duration: 1000,
        easing: "easeInOutQuart" as const,
      },
    };

    return { data: chartData, options };
  };

  const handleTimeRangePreset = (range: [dayjs.Dayjs, dayjs.Dayjs]) => {
    setDateRange(range);
  };

  useEffect(() => {
    if (visible) {
      fetchRevenueData();
    }
  }, [visible, dateRange, fetchRevenueData]);

  const chartConfig = getChartConfig();

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <i
            className="ri-line-chart-line"
            style={{ marginRight: 8, color: "#1677ff" }}
          />
          营收趋势分析
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={isMobile ? "95%" : 1200}
      centered
      styles={{
        body: {
          maxHeight: "calc(100vh - 120px)",
          overflow: "auto",
          padding: isMobile ? "12px" : "24px",
        },
      }}
    >
      <Spin spinning={loading}>
        {/* 筛选条件 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={12}>
              <div style={{ marginBottom: 8, fontSize: isMobile ? 12 : 14 }}>
                时间范围
              </div>
              <RangePicker
                style={{ width: "100%" }}
                value={dateRange}
                onChange={(dates) => {
                  if (dates) {
                    setDateRange([dates[0]!, dates[1]!]);
                  }
                }}
                format="YYYY-MM-DD"
                size={isMobile ? "small" : "middle"}
              />
            </Col>
            <Col xs={24} sm={12} md={12}>
              <div style={{ marginBottom: 8, fontSize: isMobile ? 12 : 14 }}>
                图表类型
              </div>
              <Space>
                <Button
                  size={isMobile ? "small" : "middle"}
                  type={chartType === "line" ? "primary" : "default"}
                  onClick={() => setChartType("line")}
                  icon={<i className="ri-line-chart-line" />}
                >
                  {!isMobile && "折线图"}
                </Button>
                <Button
                  size={isMobile ? "small" : "middle"}
                  type={chartType === "bar" ? "primary" : "default"}
                  onClick={() => setChartType("bar")}
                  icon={<i className="ri-bar-chart-line" />}
                >
                  {!isMobile && "柱状图"}
                </Button>
              </Space>
            </Col>
          </Row>

          {/* 快捷时间范围选择 */}
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <div
                style={{
                  marginBottom: 8,
                  fontSize: isMobile ? 12 : 13,
                  color: "#666",
                }}
              >
                快捷时间范围：
              </div>
              <Space wrap size={isMobile ? [4, 4] : [8, 8]}>
                {timeRangePresets.map((preset) => (
                  <Button
                    key={preset.label}
                    size={isMobile ? "small" : "small"}
                    type={
                      dateRange[0].isSame(preset.value[0], "day") &&
                      dateRange[1].isSame(preset.value[1], "day")
                        ? "primary"
                        : "default"
                    }
                    onClick={() => handleTimeRangePreset(preset.value)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 统计概览 */}
        {data?.summary && (
          <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic
                  title="总营收"
                  value={(data.summary.total_revenue / 100).toFixed(2)}
                  prefix="¥"
                  valueStyle={{ fontSize: isMobile ? 16 : 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic
                  title="净营收"
                  value={(data.summary.net_revenue / 100).toFixed(2)}
                  prefix="¥"
                  valueStyle={{
                    fontSize: isMobile ? 16 : 20,
                    color: "#52c41a",
                  }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic
                  title="总订单"
                  value={data.summary.total_orders}
                  suffix="单"
                  valueStyle={{ fontSize: isMobile ? 16 : 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic
                  title="成功订单"
                  value={data.summary.total_paid_orders}
                  suffix="单"
                  valueStyle={{
                    fontSize: isMobile ? 16 : 20,
                    color: "#52c41a",
                  }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic
                  title="退款订单"
                  value={data.summary.total_refund_orders}
                  suffix="单"
                  valueStyle={{
                    fontSize: isMobile ? 16 : 20,
                    color: "#ff4d4f",
                  }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small">
                <Statistic
                  title="退款金额"
                  value={(data.summary.total_refund / 100).toFixed(2)}
                  prefix="¥"
                  valueStyle={{
                    fontSize: isMobile ? 16 : 20,
                    color: "#ff4d4f",
                  }}
                />
              </Card>
            </Col>
          </Row>
        )}

        <Card
          title={`营收趋势图 (${data?.date_range || ""})`}
          size={isMobile ? "small" : "default"}
        >
          {chartConfig ? (
            <div
              style={{
                height: isMobile ? 300 : 400,
                padding: isMobile ? "5px" : "10px",
              }}
            >
              {chartType === "line" ? (
                <Line data={chartConfig.data} options={chartConfig.options} />
              ) : (
                <Bar data={chartConfig.data} options={chartConfig.options} />
              )}
            </div>
          ) : (
            <div
              style={{
                height: isMobile ? 300 : 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
              }}
            >
              暂无数据
            </div>
          )}
        </Card>
      </Spin>
    </Modal>
  );
};

export default RevenueChart;
